<template>
  <div class="merchant-management">
    <div class="header">
      <h2>商户管理</h2>
      <div class="actions">
        <el-button type="primary" @click="handleAddMerchant">
          <el-icon><Plus /></el-icon>
          添加商户
        </el-button>
        <el-button @click="refreshData">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 搜索和筛选 -->
    <el-card class="filter-card">
      <el-form :inline="true" :model="searchForm">
        <el-form-item label="商户名称">
          <el-input v-model="searchForm.name" placeholder="请输入商户名称" clearable />
        </el-form-item>
        <el-form-item label="商户状态">
          <el-select v-model="searchForm.status" placeholder="请选择状态" clearable>
            <el-option label="待审核" value="pending" />
            <el-option label="已启用" value="active" />
            <el-option label="已禁用" value="inactive" />
            <el-option label="已拒绝" value="rejected" />
          </el-select>
        </el-form-item>
        <el-form-item label="商户位置">
          <el-input v-model="searchForm.location" placeholder="请输入商户位置" clearable />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">
            <el-icon><Search /></el-icon>
            搜索
          </el-button>
          <el-button @click="resetSearch">
            <el-icon><Refresh /></el-icon>
            重置
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 商户列表 -->
    <el-card>
      <el-table :data="merchantList" v-loading="loading" stripe border>
        <el-table-column prop="id" label="ID" width="70" />
        <el-table-column prop="name" label="商户名称" min-width="150" />
        <el-table-column prop="category" label="商户类型" width="120">
          <template #default="{ row }">
            <span v-if="row.category">{{ row.category.name }}</span>
            <span v-else class="text-muted">未分类</span>
          </template>
        </el-table-column>
        <el-table-column prop="contactPerson" label="联系人" width="100" />
        <el-table-column prop="phone" label="联系电话" width="130" />
        <el-table-column prop="address" label="地址" min-width="180" />
        <el-table-column prop="status" label="状态" width="90">
          <template #default="{ row }">
            <el-tag :type="getStatusTag(row.status)" size="small">
              {{ getStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="commissionRate" label="服务费" width="80">
          <template #default="{ row }">
            {{ row.commissionRate || 3 }}%
          </template>
        </el-table-column>
        <el-table-column prop="rebateRate" label="用户分成" width="90">
          <template #default="{ row }">
            {{ (row.rebateRate * 100 || 50).toFixed(1) }}%
          </template>
        </el-table-column>
        <el-table-column prop="refereeId" label="推广员ID" width="100">
          <template #default="{ row }">
            <span v-if="row.refereeId" class="referee-link">{{ row.refereeId }}</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="160">
          <template #default="{ row }">
            {{ formatDateTime(row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="260" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleView(row)">查看</el-button>
            <el-button type="warning" size="small" @click="handleEdit(row)">编辑</el-button>
            <el-button 
              v-if="row.status === 'pending'" 
              type="success" size="small" 
              @click="handleApprove(row)">审核</el-button>
            <el-button 
              v-if="row.status === 'active'" 
              type="danger" size="small" 
              @click="handleDisable(row)">禁用</el-button>
            <el-button 
              v-if="row.status === 'inactive'" 
              type="success" size="small" 
              @click="handleEnable(row)">启用</el-button>
            <el-button type="danger" size="small" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="pagination.current"
          v-model:page-size="pagination.size"
          :total="pagination.total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 添加商户模态框 -->
    <el-dialog 
      v-model="addDialogVisible" 
      title="添加商户" 
      width="900px"
      :close-on-click-modal="false"
    >
      <el-form ref="addFormRef" :model="addForm" :rules="addRules" label-width="120px">
        <!-- 基本信息 -->
        <el-divider content-position="left">基本信息</el-divider>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="商户名称" prop="name">
              <el-input v-model="addForm.name" placeholder="请输入商户名称" maxlength="100" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="商户状态" prop="status">
              <el-select v-model="addForm.status" placeholder="请选择状态" style="width: 100%">
                <el-option label="待审核" value="pending" />
                <el-option label="已启用" value="active" />
                <el-option label="已禁用" value="inactive" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="联系人" prop="contactPerson">
              <el-input v-model="addForm.contactPerson" placeholder="请输入联系人姓名" maxlength="50" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="联系电话" prop="phone">
              <el-input v-model="addForm.phone" placeholder="请输入联系电话" maxlength="20" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="联系邮箱" prop="email">
              <el-input v-model="editForm.email" placeholder="请输入邮箱" maxlength="100" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="商户分类" prop="categoryId">
              <el-select v-model="addForm.categoryId" placeholder="请选择商户分类" clearable style="width: 100%" @change="handleCategoryChange">
                <el-option
                  v-for="cat in categoryList"
                  :key="cat.id"
                  :label="cat.name + ' (' + (cat.commissionRate * 100).toFixed(1) + '%)'"
                  :value="cat.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12" v-if="currentSubTypes.length > 0">
            <el-form-item label="细分领域" prop="industrySubType">
              <el-select v-model="addForm.industrySubType" placeholder="请选择细分领域" clearable style="width: 100%" @change="handleSubTypeChange(addForm.industrySubType, 'add')">
                <el-option 
                  v-for="sub in currentSubTypes" 
                  :key="sub.value" 
                  :label="sub.label + ' (' + (sub.rate * 100).toFixed(1) + '%抽成)'" 
                  :value="sub.value"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 法人信息 -->
        <el-divider content-position="left">法人信息</el-divider>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="法人姓名" prop="legalPerson">
              <el-input v-model="addForm.legalPerson" placeholder="请输入法人姓名" maxlength="50" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="法人身份证" prop="legalPersonIdCard">
              <el-input v-model="addForm.legalPersonIdCard" placeholder="请输入身份证号" maxlength="18" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="法人电话" prop="legalPersonPhone">
              <el-input v-model="addForm.legalPersonPhone" placeholder="请输入法人电话" maxlength="20" />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 地址信息 -->
        <el-divider content-position="left">地址信息</el-divider>
        <el-form-item label="商户地址" prop="address">
          <el-input v-model="addForm.address" placeholder="请输入商户详细地址" />
        </el-form-item>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="经度" prop="longitude">
              <el-input v-model="addForm.longitude" placeholder="如：116.397499" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="纬度" prop="latitude">
              <el-input v-model="addForm.latitude" placeholder="如：39.908722" disabled />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="地图选点">
          <MapPicker 
            v-model:latitude="addForm.latitude"
            v-model:longitude="addForm.longitude"
            placeholder="点击在地图上选择位置获取经纬度"
          />
        </el-form-item>

        <!-- 返佣设置（可覆盖全局配置） -->
        <el-divider content-position="left">返佣设置（留空使用全局配置）</el-divider>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="平台服务费" prop="commissionRate">
              <el-input v-model="addForm.commissionRate" type="number" :min="0" :max="100" step="0.1" placeholder="默认3">
                <template #append>%</template>
              </el-input>
              <div class="form-tip">消费金额的服务费比例，如3%表示100元消费抽3元</div>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="用户分成比例" prop="rebateRate">
              <el-input v-model="addForm.rebateRate" type="number" :min="0" :max="100" step="0.1" placeholder="默认50">
                <template #append>%</template>
              </el-input>
              <div class="form-tip">服务费中给用户的分成，如50%表示用户得1.5元</div>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="帮扶分比例" prop="helperRewardRate">
              <el-input v-model="addForm.helperRewardRate" type="number" :min="0" :max="100" step="0.1" placeholder="默认16">
                <template #append>%</template>
              </el-input>
              <div class="form-tip">亲友团消费时帮扶分占比，如16%</div>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 图片上传 -->
        <el-divider content-position="left">商户图片</el-divider>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="商户Logo" prop="logoUrl">
              <ImageUpload 
                v-model="addForm.logoUrl" 
                placeholder="上传Logo"
                :enable-crop="true"
                :aspect-ratio="1"
                :max-size="150"
              />
              <div class="upload-tip">支持裁剪，自动压缩至150KB</div>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="封面图" prop="coverImageUrl">
              <ImageUpload 
                v-model="addForm.coverImageUrl" 
                placeholder="上传封面"
                :enable-crop="false"
                :max-size="150"
              />
              <div class="upload-tip">自动压缩至150KB</div>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="营业执照" prop="businessLicenseUrl">
              <ImageUpload 
                v-model="addForm.businessLicenseUrl" 
                placeholder="上传营业执照"
                :enable-crop="false"
                :max-size="150"
              />
              <div class="upload-tip">自动压缩至150KB</div>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 商户简介 -->
        <el-divider content-position="left">其他信息</el-divider>
        <el-form-item label="商户简介" prop="description">
          <el-input v-model="addForm.description" type="textarea" :rows="3" placeholder="请输入商户简介（选填）" maxlength="200" show-word-limit />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="addDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSaveAdd" :loading="saving">确认添加</el-button>
      </template>
    </el-dialog>

    <!-- 查看商户模态框 -->
    <el-dialog 
      v-model="viewDialogVisible" 
      title="查看商户详情" 
      width="900px"
    >
      <el-descriptions :column="3" border>
        <el-descriptions-item label="商户名称">{{ currentMerchant?.name }}</el-descriptions-item>
        <el-descriptions-item label="商户状态">
          <el-tag :type="getStatusTag(currentMerchant?.status)">{{ getStatusText(currentMerchant?.status) }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="商户分类">
          {{ currentMerchant?.category?.name || '未分类' }}
        </el-descriptions-item>
        <el-descriptions-item label="联系人">{{ currentMerchant?.contactPerson || '-' }}</el-descriptions-item>
        <el-descriptions-item label="联系电话">{{ currentMerchant?.phone || '-' }}</el-descriptions-item>
        <el-descriptions-item label="联系邮箱">{{ currentMerchant?.email || '-' }}</el-descriptions-item>
        <el-descriptions-item label="商户地址" :span="3">{{ currentMerchant?.address || '-' }}</el-descriptions-item>
        <el-descriptions-item label="经度">{{ currentMerchant?.longitude || '-' }}</el-descriptions-item>
        <el-descriptions-item label="纬度">{{ currentMerchant?.latitude || '-' }}</el-descriptions-item>
        <el-descriptions-item label="法人姓名">{{ currentMerchant?.legalPerson || '-' }}</el-descriptions-item>
        <el-descriptions-item label="法人身份证">{{ maskIdCard(currentMerchant?.legalPersonIdCard) }}</el-descriptions-item>
        <el-descriptions-item label="法人电话">{{ currentMerchant?.legalPersonPhone || '-' }}</el-descriptions-item>
        <el-descriptions-item label="平台服务费">{{ currentMerchant?.commissionRate || 3 }}%</el-descriptions-item>
        <el-descriptions-item label="用户分成">{{ (currentMerchant?.rebateRate * 100 || 50).toFixed(1) }}%</el-descriptions-item>
        <el-descriptions-item label="帮扶分比例">{{ currentMerchant?.helperRewardRate ? (currentMerchant.helperRewardRate * 100).toFixed(1) + '%' : '默认' }}</el-descriptions-item>
        <el-descriptions-item label="推广员ID">{{ currentMerchant?.refereeId || '-' }}</el-descriptions-item>
        <el-descriptions-item label="推广任务ID">{{ currentMerchant?.refereeTaskId || '-' }}</el-descriptions-item>
        <el-descriptions-item label="首单时间">{{ formatDateTime(currentMerchant?.firstOrderAt) || '-' }}</el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ formatDateTime(currentMerchant?.createdAt) }}</el-descriptions-item>
        <el-descriptions-item label="更新时间">{{ formatDateTime(currentMerchant?.updatedAt) }}</el-descriptions-item>
        <el-descriptions-item label="总销售额">¥{{ currentMerchant?.totalSales || 0 }}</el-descriptions-item>
        <el-descriptions-item label="已付佣金">¥{{ currentMerchant?.totalCommissionPaid || 0 }}</el-descriptions-item>
        <el-descriptions-item label="待付佣金">¥{{ currentMerchant?.totalCommissionPending || 0 }}</el-descriptions-item>
      </el-descriptions>
      
      <el-divider content-position="left">商户简介</el-divider>
      <p>{{ currentMerchant?.description || '暂无简介' }}</p>
      
      <el-divider content-position="left">商户图片</el-divider>
      <el-row :gutter="20">
        <el-col :span="8" v-if="currentMerchant?.logoUrl">
          <el-image :src="currentMerchant.logoUrl" fit="cover" style="width: 100%; height: 150px" />
          <div class="image-label">商户Logo</div>
        </el-col>
        <el-col :span="8" v-if="currentMerchant?.coverImageUrl">
          <el-image :src="currentMerchant.coverImageUrl" fit="cover" style="width: 100%; height: 150px" />
          <div class="image-label">封面图</div>
        </el-col>
        <el-col :span="8" v-if="currentMerchant?.businessLicenseUrl">
          <el-image :src="currentMerchant.businessLicenseUrl" fit="contain" style="width: 100%; height: 150px" />
          <div class="image-label">营业执照</div>
        </el-col>
      </el-row>
    </el-dialog>

    <!-- 编辑商户模态框 -->
    <el-dialog 
      v-model="editDialogVisible" 
      title="编辑商户" 
      width="900px"
      :close-on-click-modal="false"
    >
      <el-form ref="editFormRef" :model="editForm" :rules="editRules" label-width="120px">
        <!-- 基本信息 -->
        <el-divider content-position="left">基本信息</el-divider>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="商户名称" prop="name">
              <el-input v-model="editForm.name" placeholder="请输入商户名称" maxlength="100" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="商户状态" prop="status">
              <el-select v-model="editForm.status" placeholder="请选择状态" style="width: 100%">
                <el-option label="待审核" value="pending" />
                <el-option label="已启用" value="active" />
                <el-option label="已禁用" value="inactive" />
                <el-option label="已拒绝" value="rejected" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="联系人" prop="contactPerson">
              <el-input v-model="editForm.contactPerson" placeholder="请输入联系人姓名" maxlength="50" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="联系电话" prop="phone">
              <el-input v-model="editForm.phone" placeholder="请输入联系电话" maxlength="20" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="联系邮箱" prop="email">
              <el-input v-model="editForm.email" placeholder="请输入邮箱" maxlength="100" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="商户分类" prop="categoryId">
              <el-select v-model="editForm.categoryId" placeholder="请选择商户分类" clearable style="width: 100%" @change="handleCategoryChange">
                <el-option
                  v-for="cat in categoryList"
                  :key="cat.id"
                  :label="cat.name + ' (' + (cat.commissionRate * 100).toFixed(1) + '%)'"
                  :value="cat.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12" v-if="currentSubTypes.length > 0">
            <el-form-item label="细分领域" prop="industrySubType">
              <el-select v-model="editForm.industrySubType" placeholder="请选择细分领域" clearable style="width: 100%" @change="handleSubTypeChange(editForm.industrySubType, 'edit')">
                <el-option 
                  v-for="sub in currentSubTypes" 
                  :key="sub.value" 
                  :label="sub.label + ' (' + (sub.rate * 100).toFixed(1) + '%抽成)'" 
                  :value="sub.value"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 法人信息 -->
        <el-divider content-position="left">法人信息</el-divider>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="法人姓名" prop="legalPerson">
              <el-input v-model="editForm.legalPerson" placeholder="请输入法人姓名" maxlength="50" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="法人身份证" prop="legalPersonIdCard">
              <el-input v-model="editForm.legalPersonIdCard" placeholder="请输入身份证号" maxlength="18" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="法人电话" prop="legalPersonPhone">
              <el-input v-model="editForm.legalPersonPhone" placeholder="请输入法人电话" maxlength="20" />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 地址信息 -->
        <el-divider content-position="left">地址信息</el-divider>
        <el-form-item label="商户地址" prop="address">
          <el-input v-model="editForm.address" placeholder="请输入商户详细地址" />
        </el-form-item>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="经度" prop="longitude">
              <el-input v-model="editForm.longitude" placeholder="如：116.397499" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="纬度" prop="latitude">
              <el-input v-model="editForm.latitude" placeholder="如：39.908722" disabled />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="地图选点">
          <MapPicker 
            v-model:latitude="editForm.latitude"
            v-model:longitude="editForm.longitude"
            placeholder="点击在地图上选择位置获取经纬度"
          />
        </el-form-item>

        <!-- 返佣设置 -->
        <el-divider content-position="left">返佣设置（留空使用全局配置）</el-divider>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="平台服务费" prop="commissionRate">
              <el-input v-model="editForm.commissionRate" type="number" :min="0" :max="100" step="0.1" placeholder="默认3">
                <template #append>%</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="用户分成比例" prop="rebateRate">
              <el-input v-model="editForm.rebateRate" type="number" :min="0" :max="100" step="0.1" placeholder="默认50">
                <template #append>%</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="帮扶分比例" prop="helperRewardRate">
              <el-input v-model="editForm.helperRewardRate" type="number" :min="0" :max="100" step="0.1" placeholder="默认16">
                <template #append>%</template>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 图片上传 -->
        <el-divider content-position="left">商户图片</el-divider>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="商户Logo" prop="logoUrl">
              <ImageUpload 
                v-model="editForm.logoUrl" 
                placeholder="上传Logo"
                :enable-crop="true"
                :aspect-ratio="1"
                :max-size="150"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="封面图" prop="coverImageUrl">
              <ImageUpload 
                v-model="editForm.coverImageUrl" 
                placeholder="上传封面"
                :enable-crop="false"
                :max-size="150"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="营业执照" prop="businessLicenseUrl">
              <ImageUpload 
                v-model="editForm.businessLicenseUrl" 
                placeholder="上传营业执照"
                :enable-crop="false"
                :max-size="150"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 商户简介 -->
        <el-divider content-position="left">其他信息</el-divider>
        <el-form-item label="商户简介" prop="description">
          <el-input v-model="editForm.description" type="textarea" :rows="3" placeholder="请输入商户简介" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSaveEdit" :loading="saving">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Refresh, Search } from '@element-plus/icons-vue'
import { merchantApi } from '@/api/modules/merchant.js'
import { getPublicCategories } from '@/api/modules/merchant-category.js'
import ImageUpload from '@/components/common/ImageUpload.vue'
import MapPicker from '@/components/common/MapPicker.vue'

// 状态管理
const loading = ref(false)
const saving = ref(false)
const viewDialogVisible = ref(false)
const editDialogVisible = ref(false)
const addDialogVisible = ref(false)
const currentMerchant = ref(null)
const addFormRef = ref()
const editFormRef = ref()

// 商户列表数据
const merchantList = ref([])

// 商户分类列表
const categoryList = ref([])

// 细分领域选项（根据分类动态显示）
// 当前可选的细分领域（从分类数据中获取）
const currentSubTypes = ref([])


// 搜索表单
const searchForm = reactive({
  name: '',
  status: '',
  location: ''
})

// 添加商户表单
const addForm = reactive({
  name: '',
  contactPerson: '',
  phone: '',
  email: '',
  address: '',
  categoryId: null,
  industrySubType: null,
  latitude: '',
  longitude: '',
  legalPerson: '',
  legalPersonIdCard: '',
  legalPersonPhone: '',
  businessLicenseUrl: '',
  commissionRate: null,
  rebateRate: null,
  helperRewardRate: null,
  logoUrl: '',
  coverImageUrl: '',
  description: '',
  status: 'pending'
})

// 编辑商户表单
const editForm = reactive({
  id: null,
  name: '',
  contactPerson: '',
  phone: '',
  email: '',
  address: '',
  categoryId: null,
  industrySubType: null,
  latitude: '',
  longitude: '',
  legalPerson: '',
  legalPersonIdCard: '',
  legalPersonPhone: '',
  businessLicenseUrl: '',
  commissionRate: null,
  rebateRate: null,
  helperRewardRate: null,
  logoUrl: '',
  coverImageUrl: '',
  description: '',
  status: 'active'
})

// 添加表单验证规则
const addRules = {
  name: [{ required: true, message: '请输入商户名称', trigger: 'blur' }],
  contactPerson: [{ required: true, message: '请输入联系人', trigger: 'blur' }],
  phone: [
    { required: true, message: '请输入联系电话', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
  ],
  status: [{ required: true, message: '请选择状态', trigger: 'change' }],
  legalPersonIdCard: [
    { pattern: /^[1-9]\d{5}(18|19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dXx]$/, message: '请输入正确的身份证号', trigger: 'blur' }
  ],
  legalPersonPhone: [
    { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
  ]
}

// 编辑表单验证规则
const editRules = {
  name: [{ required: true, message: '请输入商户名称', trigger: 'blur' }],
  contactPerson: [{ required: true, message: '请输入联系人', trigger: 'blur' }],
  phone: [{ required: true, message: '请输入联系电话', trigger: 'blur' }],
  status: [{ required: true, message: '请选择状态', trigger: 'change' }]
}

// 分页配置
const pagination = reactive({
  current: 1,
  size: 10,
  total: 0
})

// 获取商户列表
const fetchMerchants = async () => {
  try {
    loading.value = true
    const response = await merchantApi.getMerchants({
      page: pagination.current,
      size: pagination.size,
      ...searchForm
    })
    if (response.code === 200 && response.data) {
      merchantList.value = response.data.list || []
      pagination.total = response.data.total || 0
    } else if (response.code === 401) {
      localStorage.removeItem('admin-token')
      localStorage.removeItem('admin-info')
      window.location.href = '/#/login'
    } else {
      ElMessage.error(response.message || '获取商户列表失败')
    }
  } catch (error) {
    ElMessage.error('获取商户列表异常')
  } finally {
    loading.value = false
  }
}

// 获取商户分类列表
const fetchCategories = async () => {
  try {
    const response = await getPublicCategories()
    if (response && response.length > 0) {
      categoryList.value = response
    }
  } catch (error) {
    console.error('获取分类列表失败', error)
  }
}

// 方法
const getStatusText = (status) => {
  const map = { pending: '待审核', active: '已启用', inactive: '已禁用', rejected: '已拒绝' }
  return map[status] || status
}

const getStatusTag = (status) => {
  const map = { pending: 'warning', active: 'success', inactive: 'danger', rejected: 'info' }
  return map[status] || ''
}



// 处理分类变化
const handleCategoryChange = (categoryId) => {
  // 清空细分领域
  addForm.industrySubType = null
  editForm.industrySubType = null
  
  // 获取选中的分类的subTypes
  const category = categoryList.value.find(c => c.id === categoryId)
  if (category && category.subTypes && category.subTypes.length > 0) {
    currentSubTypes.value = category.subTypes
  } else {
    currentSubTypes.value = []
  }
}
// 处理细分领域变化 - 自动填充抽成比例
const handleSubTypeChange = (subType, formType) => {
  const sub = currentSubTypes.value.find(s => s.value === subType)
  if (sub) {
    if (formType === 'add') {
      addForm.commissionRate = sub.rate * 100
    } else {
      editForm.commissionRate = sub.rate * 100
    }
  }
}

const formatDateTime = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN')
}

// 身份证脱敏
const maskIdCard = (idCard) => {
  if (!idCard) return '-'
  return idCard.replace(/(\d{6})\d{8}(\d{4})/, '$1********$2')
}

const refreshData = () => { fetchMerchants() }
const handleSearch = () => { pagination.current = 1; fetchMerchants() }
const resetSearch = () => { 
  Object.keys(searchForm).forEach(key => { searchForm[key] = '' })
  handleSearch()
}
const handleSizeChange = (size) => { pagination.size = size; fetchMerchants() }
const handleCurrentChange = (page) => { pagination.current = page; fetchMerchants() }

// 重置添加表单
const resetAddForm = () => {
  Object.assign(addForm, {
    name: '',
    contactPerson: '',
    phone: '',
    email: '',
    address: '',
    categoryId: null,
    industrySubType: null,
    latitude: '',
    longitude: '',
    legalPerson: '',
    legalPersonIdCard: '',
    legalPersonPhone: '',
    businessLicenseUrl: '',
    commissionRate: null,
    rebateRate: null,
    helperRewardRate: null,
    logoUrl: '',
    coverImageUrl: '',
    description: '',
    status: 'pending'
  })
}

// 打开添加商户模态框
const handleAddMerchant = () => {
  resetAddForm()
  addDialogVisible.value = true
}

// 保存添加商户
const handleSaveAdd = async () => {
  if (!addFormRef.value) return
  try {
    await addFormRef.value.validate()
    saving.value = true

    const submitData = {
      name: addForm.name,
      contactPerson: addForm.contactPerson,
      phone: addForm.phone,
      email: addForm.email || null,
      address: addForm.address,
      categoryId: addForm.categoryId || null,
      industrySubType: addForm.industrySubType || null,
      latitude: addForm.latitude ? parseFloat(addForm.latitude) : null,
      longitude: addForm.longitude ? parseFloat(addForm.longitude) : null,
      legalPerson: addForm.legalPerson || null,
      legalPersonIdCard: addForm.legalPersonIdCard || null,
      legalPersonPhone: addForm.legalPersonPhone || null,
      businessLicenseUrl: addForm.businessLicenseUrl || null,
      commissionRate: parseFloat(addForm.commissionRate) || null,
      rebateRate: (parseFloat(addForm.rebateRate) || 50) / 100,
      helperRewardRate: addForm.helperRewardRate ? parseFloat(addForm.helperRewardRate) / 100 : null,
      logoUrl: addForm.logoUrl || null,
      coverImageUrl: addForm.coverImageUrl || null,
      description: addForm.description,
      status: addForm.status,
      isActive: addForm.status === 'active' ? 1 : 0
    }

    const response = await merchantApi.createMerchant(submitData)
    if (response.code === 200) {
      ElMessage.success('商户添加成功')
      addDialogVisible.value = false
      fetchMerchants()
    } else {
      ElMessage.error(response.message || '添加失败')
    }
  } catch (error) {
    console.error('添加商户失败', error)
  } finally {
    saving.value = false
  }
}

// 查看商户详情
const handleView = (row) => {
  currentMerchant.value = row
  viewDialogVisible.value = true
}

// 编辑商户
const handleEdit = (row) => {
  editForm.id = row.id
  editForm.name = row.name || ''
  editForm.contactPerson = row.contactPerson || ''
  editForm.phone = row.phone || ''
  editForm.email = row.email || ''
  editForm.address = row.address || ''
  editForm.categoryId = row.categoryId || null
  editForm.industrySubType = row.industrySubType || null
  // 设置细分领域选项
  const category = categoryList.value.find(c => c.id === row.categoryId)
  if (category && subTypeOptions[category.name]) {
    currentSubTypes.value = subTypeOptions[category.name]
  } else {
    currentSubTypes.value = []
  }
  editForm.latitude = row.latitude || ''
  editForm.longitude = row.longitude || ''
  editForm.legalPerson = row.legalPerson || ''
  editForm.legalPersonIdCard = row.legalPersonIdCard || ''
  editForm.legalPersonPhone = row.legalPersonPhone || ''
  editForm.businessLicenseUrl = row.businessLicenseUrl || ''
  editForm.commissionRate = row.commissionRate || null
  editForm.rebateRate = row.rebateRate ? (row.rebateRate * 100).toFixed(1) : null
  editForm.helperRewardRate = row.helperRewardRate ? (row.helperRewardRate * 100).toFixed(1) : null
  editForm.logoUrl = row.logoUrl || ''
  editForm.coverImageUrl = row.coverImageUrl || ''
  editForm.description = row.description || ''
  editForm.status = row.status || 'active'
  editDialogVisible.value = true
}

// 保存编辑
const handleSaveEdit = async () => {
  if (!editFormRef.value) return
  try {
    await editFormRef.value.validate()
    saving.value = true

    const submitData = {
      name: editForm.name,
      contactPerson: editForm.contactPerson,
      phone: editForm.phone,
      email: editForm.email || null,
      address: editForm.address,
      categoryId: editForm.categoryId || null,
      industrySubType: editForm.industrySubType || null,
      latitude: editForm.latitude ? parseFloat(editForm.latitude) : null,
      longitude: editForm.longitude ? parseFloat(editForm.longitude) : null,
      legalPerson: editForm.legalPerson || null,
      legalPersonIdCard: editForm.legalPersonIdCard || null,
      legalPersonPhone: editForm.legalPersonPhone || null,
      businessLicenseUrl: editForm.businessLicenseUrl || null,
      commissionRate: parseFloat(editForm.commissionRate) || null,
      rebateRate: (parseFloat(editForm.rebateRate) || 50) / 100,
      helperRewardRate: editForm.helperRewardRate ? parseFloat(editForm.helperRewardRate) / 100 : null,
      logoUrl: editForm.logoUrl || null,
      coverImageUrl: editForm.coverImageUrl || null,
      description: editForm.description,
      status: editForm.status,
      isActive: editForm.status === 'active' ? 1 : 0
    }

    const response = await merchantApi.updateMerchant(editForm.id, submitData)
    if (response.code === 200) {
      ElMessage.success('商户更新成功')
      editDialogVisible.value = false
      fetchMerchants()
    } else {
      ElMessage.error(response.message || '更新失败')
    }
  } catch (error) {
    console.error('更新商户失败', error)
  } finally {
    saving.value = false
  }
}

// 审核商户
const handleApprove = async (row) => {
  try {
    await ElMessageBox.confirm('确定要通过审核吗？', '审核确认', { type: 'warning' })
    const response = await merchantApi.updateMerchantStatus(row.id, 'active')
    if (response.code === 200) {
      ElMessage.success('审核通过')
      fetchMerchants()
    } else {
      ElMessage.error(response.message || '审核失败')
    }
  } catch (error) {
    if (error !== 'cancel') ElMessage.error('审核失败')
  }
}

// 禁用商户
const handleDisable = async (row) => {
  try {
    await ElMessageBox.confirm('确定要禁用这个商户吗？', '禁用确认', { type: 'warning' })
    const response = await merchantApi.updateMerchantStatus(row.id, 'inactive')
    if (response.code === 200) {
      ElMessage.success('商户已禁用')
      fetchMerchants()
    } else {
      ElMessage.error(response.message || '禁用失败')
    }
  } catch (error) {
    if (error !== 'cancel') ElMessage.error('禁用失败')
  }
}

// 启用商户
const handleEnable = async (row) => {
  try {
    await ElMessageBox.confirm('确定要启用这个商户吗？', '启用确认', { type: 'warning' })
    const response = await merchantApi.updateMerchantStatus(row.id, 'active')
    if (response.code === 200) {
      ElMessage.success('商户已启用')
      fetchMerchants()
    } else {
      ElMessage.error(response.message || '启用失败')
    }
  } catch (error) {
    if (error !== 'cancel') ElMessage.error('启用失败')
  }
}

// 删除商户
const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(
      '确定要删除商户"' + row.name + '"吗？此操作不可恢复！',
      '删除确认',
      { type: 'warning' }
    )
    const response = await merchantApi.deleteMerchant(row.id)
    if (response.code === 200) {
      ElMessage.success('商户已删除')
      fetchMerchants()
    } else {
      ElMessage.error(response.message || '删除失败')
    }
  } catch (error) {
    if (error !== 'cancel') ElMessage.error('删除失败')
  }
}

onMounted(() => { fetchMerchants(); fetchCategories() })
</script>

<style scoped>
.merchant-management {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header h2 {
  margin: 0;
  font-size: 20px;
  color: #ffffff;
}

.actions {
  display: flex;
  gap: 10px;
}

.filter-card {
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.referee-link {
  color: #409eff;
  cursor: pointer;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  line-height: 1.4;
}

.upload-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.text-muted {
  color: #909399;
}

.image-label {
  text-align: center;
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
}
</style>
